/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from '@mui/joy';
import { defaultDatetimeLocalValue } from '../utils/trackingEventControl';

/**
 * Modal flow for optional tracking-event control on entity saves.
 * Step 1: Skip / Record / Auto (omit).
 * Step 2 (Record): datetime picker then confirm.
 */
export default function TrackingEventPromptModal({
  open,
  onClose,
  onResolve,
}) {
  const { t } = useTranslation('ui');
  const [step, setStep] = useState('prompt');
  const [timestampLocal, setTimestampLocal] = useState(defaultDatetimeLocalValue);
  const [timestampError, setTimestampError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep('prompt');
    setTimestampLocal(defaultDatetimeLocalValue());
    setTimestampError('');
  }, [open]);

  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
  };

  const confirmRecord = () => {
    if (!timestampLocal || !String(timestampLocal).trim()) {
      setTimestampError(t('trackingEventControl.timestampRequired'));
      return;
    }
    if (typeof onResolve === 'function') {
      onResolve({ mode: 'record', timestampLocal });
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog variant="outlined" sx={{ maxWidth: 440, width: '100%' }}>
        <ModalClose onClick={handleClose} />
        <DialogTitle>
          {step === 'prompt'
            ? t('trackingEventControl.title')
            : t('trackingEventControl.timestampTitle')}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {step === 'prompt' ? (
            <Typography level="body-md">
              {t('trackingEventControl.body')}
            </Typography>
          ) : (
            <FormControl sx={{ mt: 0.5 }}>
              <FormLabel>{t('trackingEventControl.timestampLabel')}</FormLabel>
              <Input
                type="datetime-local"
                value={timestampLocal}
                onChange={(e) => {
                  setTimestampLocal(e.target.value);
                  setTimestampError('');
                }}
                slotProps={{ input: { step: 60 } }}
              />
              <FormHelperText>
                {t('trackingEventControl.timestampHint')}
              </FormHelperText>
              {timestampError ? (
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {timestampError}
                </FormHelperText>
              ) : null}
            </FormControl>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {step === 'prompt' ? (
            <>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => onResolve?.({ mode: 'skip' })}
              >
                {t('trackingEventControl.skip')}
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={() => setStep('timestamp')}
              >
                {t('trackingEventControl.record')}
              </Button>
              <Box sx={{ flexBasis: '100%', mt: 0.5 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => onResolve?.({ mode: 'omit' })}
                >
                  {t('trackingEventControl.auto')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Button variant="plain" color="neutral" onClick={() => setStep('prompt')}>
                {t('actions.back', { defaultValue: 'Back' })}
              </Button>
              <Button variant="solid" color="primary" onClick={confirmRecord}>
                {t('trackingEventControl.confirmRecord')}
              </Button>
            </>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
